import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { ModalController, NavParams } from '@ionic/angular';

import { languageSupport, languageService } from '@app/modules/common/language';
import { WSConnection } from '@app/modules/sockets';
import { UiReplenishModal } from '@app/components/model/ticket/ui-replenish/ui-replenish';
import { UiProsegurChangeModal } from '@app/components/model/ticket/ui-prosegur-change/ui-prosegur-change';

interface DispRow {
    fv: number;          // valor facial (céntimos)
    label: string;       // "5.00", "0.50"…
    recycler: number;    // piezas en el RECICLADOR (dispensable)
    stacker: number;     // piezas en el STACKER
    bag: number;         // piezas en la BOLSA
    dispensable: number; // = recycler (compat)
    stored: number;      // = stacker + bag (compat)
    total: number;       // piezas totales en máquina
    amount: number;      // importe de la denominación (euros)
    devid: number;       // 1 = billete, 2 = moneda
}

interface CollectRow {
    fv: number;          // valor facial (céntimos)
    label: string;       // "5.00"
    dispensable: number; // máximo que se puede sacar (reciclador)
    devid: number;       // 1 = billete, 2 = moneda
    request: number;     // piezas que el operario quiere sacar
}

/**
 * "Backoffice" de Prosegur (cajón inteligente): panel de administración del
 * cajón agrupando las acciones sueltas del control de caja (igual que el
 * backoffice de Glory, que Glory ya consolidó en un solo botón).
 *
 * Vistas:
 *  - `menu`     → inventario DISPENSABLE (reciclador, vía CONTENTS) + acciones:
 *                 Cargar máquina (Replenish → reciclador), Cambio (billete→
 *                 monedas), Sacar efectivo, Actualizar.
 *  - `dispense` → "sacar efectivo": importe manual o "sacar todo" lo dispensable,
 *                 con pre-check contra el inventario y lectura de
 *                 totalDispensed/totalNotDispensed (evita el cuelgue del CASH_OUT).
 *
 * El backend (`prosegur.rs`) mapea CONTENTS (Balance→recyclable), START/CHECK/
 * FINISH_REPLENISH, DISPENSE/CASH_OUT y DISPENSE_COINS. El llenado (Replenish)
 * queda en el RECICLADOR (dispensable), según la doc MDW 1.1.6 (FinishReplenish).
 *
 * PIN: validación en cliente (el Balance de Prosegur no tiene gate propio),
 * por defecto 1515, configurable en `drawer.backofficePin`.
 */
@Component({
    selector: 'ui-modal-prosegur-backoffice',
    templateUrl: './ui-prosegur-backoffice.html',
    styleUrls: [ './ui-prosegur-backoffice.scss' ]
})
export class UiProsegurBackofficeModal extends languageSupport implements OnInit, OnDestroy {
    public _connection: WSConnection = null;
    public _target: string = 'PROSEGUR';
    private _pin: string = '1515';

    public status: 'pin' | 'loading' | 'ready' | 'error' = 'pin';
    public view: 'menu' | 'dispense' = 'menu';
    public pinInput: string = '';
    public pinError: boolean = false;
    public errorMessage: string = null;

    public notes: DispRow[] = [];
    public coins: DispRow[] = [];
    public totalRecycler: number = 0;      // euros (reciclador / dispensable)
    public totalStacker: number = 0;       // euros (stacker)
    public totalBag: number = 0;           // euros (bolsa)
    public grandTotal: number = 0;         // euros (total en máquina)

    public busy: boolean = false;
    public actionMsg: string = null;
    public actionOk: boolean = false;

    // Sacar efectivo (selector por denominación, estilo Glory Collect).
    public collectClass: 'notes' | 'coins' | 'both' = 'both';
    public collectRows: CollectRow[] = [];

    // Callback del padre (ui-tab-cash) para REGISTRAR en el arqueo cada retirada
    // (Sacar efectivo -> salida) o carga (Cargar máquina -> entrada).
    private _recordMovement: ((eur: number, type: 'withdrawal' | 'load') => void) | null = null;

    constructor(private lang: languageService, private change: ChangeDetectorRef, private modalCtrl: ModalController, private params: NavParams) {
        super(lang, change);
        this._connection = this.params.get('connection');
        this._target = this.params.get('target') || 'PROSEGUR';
        const _p = this.params.get('pin');
        if (_p != null && String(_p).length > 0){
            this._pin = String(_p);
        }
        this._recordMovement = this.params.get('recordMovement') || null;
    }

    // Inventario DISPENSABLE (reciclador) por denominación en céntimos { fv: piezas }
    // para que el arqueo Entrada/Salida no vuelva a pedir CONTENTS (#2).
    private _inventoryMap(): { [fv: number]: number } {
        let _out: { [fv: number]: number } = {};
        for (let _r of [...(this.notes || []), ...(this.coins || [])]){
            if (_r && _r.fv > 0 && _r.recycler > 0){
                _out[_r.fv] = (_out[_r.fv] || 0) + _r.recycler;
            }
        }
        return _out;
    }

    ngOnInit() {
        this.status = 'pin';
    }

    ngOnDestroy() {
        super.OnDestroy();
    }

    /*****************************/
    /* PIN (validación cliente)  */
    /*****************************/
    OnPinKey(digit: string) {
        if (this.pinInput.length >= 8){ return; }
        this.pinInput += digit;
        this.pinError = false;
        this.change.markForCheck();
    }
    OnPinBack() {
        this.pinInput = this.pinInput.slice(0, -1);
        this.pinError = false;
        this.change.markForCheck();
    }
    async OnPinSubmit() {
        // La validación la hace el BACKEND (env PROSEGUR_PIN, configurable, igual
        // que Glory con GLORY_PIN): mandamos el PIN en el comando BACKOFFICE y, si
        // el backend responde code:'PIN', volvemos a pedirlo. Así el gate vale de
        // verdad y respeta el PIN configurado en el print_service.
        if (!this.pinInput || this.pinInput.length === 0){ return; }
        this._pin = this.pinInput;
        await this._load();
    }

    OnClose() {
        // No cerrar con una operación de dinero en curso (dispense/replenish) NI
        // mientras carga (STATUS/BACKOFFICE en vuelo): cerrar desconectaría la
        // conexión compartida dejando el comando sin respuesta y permitiría lanzar
        // otra operación contra el mismo cajón. `busy` cubre el dispense; el
        // 'loading' cubre el arranque (STATUS/BACKOFFICE).
        if (this.busy || this.status === 'loading'){ return; }
        this.modalCtrl.dismiss();
    }

    // "Entrada/Salida de efectivo" (arqueo): la rejilla vive en ui-tab-cash, así
    // que cerramos el backoffice señalando al control de caja que la abra.
    OnCashInOut() {
        if (this.busy){ return; }
        // Inventario para el arqueo (#2) SOLO si la carga fue OK ('ready'); si
        // falló, lo omitimos para que el arqueo re-lea (no ceros/stale).
        let _inv = (this.status === 'ready') ? this._inventoryMap() : null;
        this.modalCtrl.dismiss({ action: 'cashinout', inventory: _inv });
    }

    async OnRefresh() {
        if (this.busy){ return; }
        await this._load();
    }

    /*****************************/
    /* ACCIONES DE MENÚ          */
    /*****************************/
    // "Cargar máquina" (Replenish desde la entrada): abre el modal Start/Finish
    // reutilizando la conexión. El dinero queda en el RECICLADOR. Al cerrar,
    // refresca el inventario.
    async OnReplenish() {
        if (this.busy || !this._connection){ return; }
        let modal = await this.modalCtrl.create({
            component: UiReplenishModal,
            cssClass: 'modal-cashguard',
            backdropDismiss: false,
            componentProps: { connection: this._connection, target: this._target }
        });
        await modal.present();
        let _res = await modal.onDidDismiss();
        // Carga = ENTRADA de efectivo: registrar el importe cargado en el arqueo.
        let _loaded = (_res && _res.data && Number(_res.data.totalLoaded)) || 0;
        if (_loaded > 0){
            this._recordMovement?.(_loaded, 'load');
        }
        await this._load();
    }

    // "Cambio" (billete → monedas): abre el modal de cambio reutilizando la
    // conexión. Al cerrar, refresca el inventario.
    async OnChange() {
        if (this.busy || !this._connection){ return; }
        let modal = await this.modalCtrl.create({
            component: UiProsegurChangeModal,
            cssClass: 'modal-cashguard',
            backdropDismiss: false,
            componentProps: { connection: this._connection, target: this._target }
        });
        await modal.present();
        await modal.onDidDismiss();
        await this._load();
    }

    /*****************************/
    /* SACAR EFECTIVO (COLLECT)  */
    /*****************************/
    // Selector por denominación (estilo Glory Collect): el operario elige cuántas
    // piezas de cada denominación DISPENSABLE (reciclador) sacar, o "Sacar todo".
    // Del stacker/bolsa no se puede sacar sin abrir el cajón, así que solo se
    // ofrece lo dispensable.
    OnOpenDispense() {
        this.actionMsg = null;
        this.setCollectClass('both');
        this.view = 'dispense';
        this.change.markForCheck();
    }
    OnBackToMenu() {
        this.view = 'menu';
        this.actionMsg = null;
        this.change.markForCheck();
    }

    // Construye las filas del selector con el DISPENSABLE (reciclador) de la clase.
    setCollectClass(k: 'notes' | 'coins' | 'both') {
        this.collectClass = k;
        const src: DispRow[] = k === 'notes' ? this.notes
            : k === 'coins' ? this.coins
            : [...this.notes, ...this.coins];
        // Mostrar TODAS las denominaciones aunque el reciclador esté a 0 (los
        // billetes que están solo en stacker/cassette salen con Disponible 0):
        // así el operario ve el listado completo tal cual lo devuelve el back y
        // entiende por qué no puede sacar los grandes. Solo se dispensa del
        // RECICLADOR; el stepper queda deshabilitado cuando dispensable=0.
        this.collectRows = src
            .map(r => ({ fv: r.fv, label: r.label, dispensable: r.recycler, devid: r.devid, request: 0 }));
        this.change.markForCheck();
    }

    incRequest(row: CollectRow) {
        if (row.request < row.dispensable){ row.request++; this.change.markForCheck(); }
    }
    decRequest(row: CollectRow) {
        if (row.request > 0){ row.request--; this.change.markForCheck(); }
    }
    setMaxRequest(row: CollectRow) {
        row.request = row.dispensable; this.change.markForCheck();
    }
    clearRequest(row: CollectRow) {
        row.request = 0; this.change.markForCheck();
    }

    get CollectPieces(): number { return this.collectRows.reduce((a, r) => a + r.request, 0); }
    get CollectAmount(): number { return this.collectRows.reduce((a, r) => a + r.request * r.fv, 0) / 100; }
    get CanCollect(): boolean { return !this.busy && this.CollectPieces > 0; }

    // Sacar exactamente las piezas elegidas (DISPENSE_BY_DENOMINATION).
    async OnCollectStart() {
        if (!this.CanCollect){ return; }
        const denominations = this.collectRows
            .filter(r => r.request > 0)
            .map(r => ({ denomination: r.fv, numItems: r.request }));
        await this._dispenseDenoms(denominations);
    }

    // "Sacar todo": todo el dispensable de la clase seleccionada.
    async OnCollectAll() {
        const src: DispRow[] = this.collectClass === 'notes' ? this.notes
            : this.collectClass === 'coins' ? this.coins
            : [...this.notes, ...this.coins];
        const denominations = src
            .filter(r => r.dispensable > 0)
            .map(r => ({ denomination: r.fv, numItems: r.dispensable }));
        if (!denominations.length){ return; }
        await this._dispenseDenoms(denominations);
    }

    // Dispensa por denominación y refresca. Lee totalDispensed/totalNotDispensed:
    // si algo no se pudo dispensar (atasco/falta), avisa con lo que SÍ salió. No
    // hace falta pre-check de exceso: el selector ya topa en el dispensable (MAX).
    private async _dispenseDenoms(denominations: Array<{ denomination: number, numItems: number }>) {
        if (this.busy || !this._connection || !denominations.length){ return; }
        this.busy = true;
        this.actionMsg = null;
        this.change.markForCheck();
        try {
            let _resp = await this._connection.Send(this._target, {
                command: 'DISPENSE_BY_DENOMINATION',
                pin: this._pin,   // propagar el PIN validado para que el backend pueda exigirlo también en la retirada, no solo en BACKOFFICE
                denominations: denominations
            });
            let _body = this._body(_resp);
            let _ok = !!(_resp && _resp.exec === 'SUCCESS' && (!_body || _body['status'] !== 'error'));
            let _notDisp = this._num(_body, 'totalNotDispensed', 0);
            let _disp = this._num(_body, 'totalDispensed', 0);
            // Sacar efectivo = SALIDA: registrar en el arqueo lo que SÍ salió
            // (aunque sea parcial), en euros.
            if (_disp > 0){
                this._recordMovement?.(_disp / 100, 'withdrawal');
            }
            let _amt = _disp > 0 ? ' · ' + (_disp / 100).toFixed(2) + this.tr('@currency') : '';
            if (_ok && _notDisp <= 0){
                this.actionOk = true;
                this.actionMsg = this.tr('@cashguard_cashout_success') + _amt;
            } else {
                // Dispensado parcial (atasco/falta): mostramos lo que SÍ salió.
                this.actionOk = false;
                this.actionMsg = this.tr('@cashguard_insufficient_cash') + _amt;
            }
        } catch (e) {
            console.error('[PROSEGUR][backoffice] dispense failed', e);
            this.actionOk = false;
            this.actionMsg = this.tr('@cashguard_cashout_error');
        }
        this.busy = false;
        this.change.markForCheck();
        await this._load();
        this.setCollectClass(this.collectClass);   // reconstruye filas con el nuevo inventario
    }

    /*****************************/
    /* INVENTARIO (CONTENTS)     */
    /*****************************/
    private async _load() {
        this.status = 'loading';
        this.change.markForCheck();
        try {
            if (!this._connection){
                this._fail(this.tr('@cashguard_connection_failed'));
                return;
            }
            // Warm-up de sesión: un STATUS establece/cachea el token del Middleware
            // (login) igual que hace el control de caja antes de CONTENTS. Sin él,
            // BACKOFFICE golpea Balance "en frío" y el login del MDW puede fallar
            // ("Failed to log in ... Verify port/service"). Best-effort: ignoramos
            // su resultado, solo calienta la sesión.
            try {
                await Promise.race([
                    this._connection.Send(this._target, { command: 'STATUS' }),
                    new Promise<null>(resolve => setTimeout(() => resolve(null), 12000))
                ]);
            } catch(_){}

            // BACKOFFICE valida el PIN en el backend (PROSEGUR_PIN) y devuelve el
            // inventario COMPLETO (reciclador/stacker/bolsa + totales). Acotado a
            // 25s para no dejar el modal colgado en "cargando".
            let _resp = await Promise.race([
                this._connection.Send(this._target, { command: 'BACKOFFICE', pin: this._pin }),
                new Promise<null>(resolve => setTimeout(() => resolve(null), 25000))
            ]);
            let _body = this._body(_resp);
            // PIN incorrecto: el backend responde code:'PIN'. Volvemos a pedirlo.
            if (_body && _body['code'] === 'PIN'){
                this.status = 'pin';
                this.pinError = true;
                this.pinInput = '';
                this.change.markForCheck();
                return;
            }
            if (!_resp || _resp.exec !== 'SUCCESS' || !_body || _body['status'] === 'error'
                || !Array.isArray(_body['denominations'])){
                this._fail(_body && _body['message']);
                return;
            }
            this._fill(_body);
            this.status = 'ready';   // la vista (menu/dispense) la fija cada llamador
        } catch (e) {
            console.error('[PROSEGUR][backoffice] load failed', e);
            this._fail();
        }
        this.change.markForCheck();
    }

    private _fill(body: any) {
        let rows: DispRow[] = (body['denominations'] || []).map((d: any) => {
            let fv = this._num(d, 'denomination', 0);        // el código YA es céntimos
            // recycler/stacker/bag por separado (con compat dispensable/stored).
            // Fallback final a `recyclable`: el comando BACKOFFICE traduce el shape
            // a recycler/stacker/bag, pero el CONTENTS crudo del MDW usa
            // `recyclable` (prosegur.rs). Sin este fallback, si llegara el shape
            // crudo el reciclador saldría 0. Solo actúa si faltan los anteriores.
            let recycler = this._num(d, 'recycler', this._num(d, 'dispensable', this._num(d, 'recyclable', 0)));
            let stacker = this._num(d, 'stacker', 0);
            let bag = this._num(d, 'bag', 0);
            // Si el back es antiguo (solo stored), repartimos stored a stacker.
            if (d['stacker'] === undefined && d['bag'] === undefined){
                stacker = this._num(d, 'stored', 0);
            }
            let total = this._num(d, 'total', recycler + stacker + bag);
            return {
                fv,
                label: (fv / 100).toFixed(2),
                recycler: recycler,
                stacker: stacker,
                bag: bag,
                dispensable: recycler,
                stored: stacker + bag,
                total: total,
                amount: this._num(d, 'amount', fv * total) / 100,
                devid: fv >= 500 ? 1 : 2
            } as DispRow;
        });
        this.notes = rows.filter(r => r.fv >= 500);
        this.coins = rows.filter(r => r.fv < 500);
        this.totalRecycler = this._num(body, 'totalRecycler', this._num(body, 'totalDispensable', 0)) / 100;
        this.totalStacker = this._num(body, 'totalStacker', 0) / 100;
        this.totalBag = this._num(body, 'totalBag', 0) / 100;
        this.grandTotal = this._num(body, 'grandTotal', 0) / 100;
    }

    ColRecycler(rows: DispRow[]): number { return rows.reduce((a, r) => a + r.recycler, 0); }
    ColStacker(rows: DispRow[]): number { return rows.reduce((a, r) => a + r.stacker, 0); }
    ColBag(rows: DispRow[]): number { return rows.reduce((a, r) => a + r.bag, 0); }
    ColTotal(rows: DispRow[]): number { return rows.reduce((a, r) => a + r.total, 0); }
    ColAmount(rows: DispRow[]): number { return rows.reduce((a, r) => a + r.amount, 0); }
    // Importe (€) por pool: fv (céntimos) × piezas de ese pool. Para la línea de
    // total por columna del pie (reciclador/stacker/bolsa en €, no solo piezas).
    ColRecyclerAmount(rows: DispRow[]): number { return rows.reduce((a, r) => a + r.fv * r.recycler, 0) / 100; }
    ColStackerAmount(rows: DispRow[]): number { return rows.reduce((a, r) => a + r.fv * r.stacker, 0) / 100; }
    ColBagAmount(rows: DispRow[]): number { return rows.reduce((a, r) => a + r.fv * r.bag, 0) / 100; }

    /*****************************/
    /* HELPERS                   */
    /*****************************/
    private _body(resp: any): any {
        let data = resp && resp['data'];
        if (typeof data === 'string'){
            try { data = JSON.parse(data); } catch (_) { return null; }
        }
        return (data && typeof data === 'object') ? data : null;
    }

    private _num(obj: any, key: string, def: number): number {
        const v = obj && obj[key];
        return (typeof v === 'number') ? v : (v !== undefined && v !== null && !isNaN(Number(v)) ? Number(v) : def);
    }

    private _fail(message?: string) {
        this.status = 'error';
        this.errorMessage = message || this.tr('@prosegur_backoffice_error');
        this.change.markForCheck();
    }
}
